import { useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ListSubheader from '@mui/material/ListSubheader';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslate } from 'src/locales';
import { WordRepeat, WordLayoutPart, WordLayoutTable, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  parts: WordLayoutPart[];
  changes: WordPlaceholderChanges;
  /** Each list's fields, as the placeholder list offers them. */
  lists: Map<string, string[]>;
  label: (path: string) => string;
  onRepeat: (repeat: WordRepeat) => void;
  onUpdate: (index: number, patch: Partial<WordRepeat>) => void;
  onRemove: (index: number) => void;
};

type Row = WordLayoutTable['rows'][number];

const cellText = (cell: { text: string }[]) => cell.map((p) => p.text).join(' ').trim();
const isBlank = (row: Row) => row.cells.every((cell) => !cellText(cell));

/** How many blank rows come straight after a row, as the server counts them. */
function blankRowsBelow(table: WordLayoutTable, row: number): number {
  let count = 0;
  table.rows.filter((r) => r.index > row).sort((a, b) => a.index - b.index).some((r) => {
    if (!isBlank(r)) return true;
    count += 1;
    return false;
  });
  return count;
}

/**
 * The document's tables, for repeating a row (staging feedback, JT1-1887): pick a row, the list it
 * repeats for, and what each cell prints. A blank row works too, as in a layout with empty
 * Employment Summary rows, and the blank rows below it can be taken out.
 */
export default function WordTablesPanel({ parts, changes, lists, label, onRepeat, onUpdate, onRemove }: Props) {
  const { t } = useTranslate();
  const [menu, setMenu] = useState<{ anchor: HTMLElement; table: WordLayoutTable; row: number } | null>(null);
  const tables = (parts.find((part) => part.kind === 'body')?.items ?? []).filter((item): item is WordLayoutTable => item.type === 'table');
  if (!tables.length) return null;

  const inParagraphRepeat = (row: Row) => row.cells.flat().some((p) => changes.repeats.some((r) => r.table === undefined && p.index >= (r.from ?? 0) && p.index <= (r.to ?? -1)));

  return (
    <Stack spacing={1.5} sx={{ mb: 2.5 }}>
      <Box>
        <Typography variant="subtitle2">{t('presentations.word.editor.tables')}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.word.editor.tables_hint')}</Typography>
      </Box>
      {tables.map((table) => {
        const columnName = (c: number) => cellText(table.rows[0]?.cells[c] ?? []) || t('presentations.word.editor.column_n', { n: c + 1 });
        // Rows a repeat above takes out.
        const takenOut = new Set<number>();
        changes.repeats.forEach((r) => {
          if (r.table !== table.index || !r.remove_empty_rows_below) return;
          for (let i = 1; i <= blankRowsBelow(table, r.row ?? 0); i += 1) takenOut.add((r.row ?? 0) + i);
        });
        return (
          <Box key={table.index} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
            <Typography variant="body2" noWrap sx={{ px: 1.25, py: 0.75, bgcolor: 'background.neutral', fontWeight: 600 }}>
              {table.rows[0]?.cells.map(cellText).filter(Boolean).join(' · ') || t('presentations.word.editor.table_n', { n: table.index + 1 })}
            </Typography>
            {table.rows.map((row) => {
              const r = changes.repeats.findIndex((repeat) => repeat.table === table.index && repeat.row === row.index);
              const repeat = r >= 0 ? changes.repeats[r] : null;
              const below = blankRowsBelow(table, row.index);
              return (
                <Box key={row.index} sx={{ px: 1.25, py: 0.75, borderTop: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: repeat ? 'primary.lighter' : undefined }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, color: isBlank(row) ? 'text.disabled' : 'text.primary', textDecoration: takenOut.has(row.index) ? 'line-through' : undefined }}>
                      {isBlank(row) ? t('presentations.word.editor.empty_row') : row.cells.map(cellText).join(' | ')}
                    </Typography>
                    {takenOut.has(row.index) && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('presentations.word.editor.taken_out')}</Typography>}
                    {repeat && <Chip size="small" color="primary" icon={<Iconify icon="solar:repeat-bold" width={14} />} label={t(`presentations.word.editor.each.${repeat.list}`)} onDelete={() => onRemove(r)} />}
                    {!repeat && !takenOut.has(row.index) && !inParagraphRepeat(row) && (
                      <Button size="small" color="inherit" onClick={(event) => setMenu({ anchor: event.currentTarget, table, row: row.index })}>
                        {t('presentations.word.editor.repeat_for')}
                      </Button>
                    )}
                  </Stack>
                  {repeat && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {row.cells.map((cell, c) => (
                        <TextField
                          // eslint-disable-next-line react/no-array-index-key
                          key={c}
                          select
                          size="small"
                          label={columnName(c)}
                          value={repeat.columns?.[c] ?? ''}
                          onChange={(event) => {
                            const columns = Array.from({ length: row.cells.length }, (_, i) => repeat.columns?.[i] ?? null);
                            columns[c] = event.target.value || null;
                            onUpdate(r, { columns });
                          }}
                        >
                          <MenuItem value="">{cellText(cell) ? t('presentations.word.editor.keep_cell') : t('presentations.word.editor.leave_empty')}</MenuItem>
                          {(lists.get(repeat.list) ?? []).map((path) => <MenuItem key={path} value={path}>{label(path)}</MenuItem>)}
                        </TextField>
                      ))}
                      {below > 0 && (
                        <FormControlLabel
                          control={<Switch size="small" checked={!!repeat.remove_empty_rows_below} onChange={(event) => onUpdate(r, { remove_empty_rows_below: event.target.checked })} />}
                          label={<Typography variant="caption">{t('presentations.word.editor.remove_empty_rows', { count: below })}</Typography>}
                        />
                      )}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
      <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        <ListSubheader sx={{ lineHeight: '32px' }}>{t('presentations.word.editor.repeat_row_for')}</ListSubheader>
        {Array.from(lists.keys()).map((list) => (
          <MenuItem
            key={list}
            onClick={() => {
              if (menu) onRepeat({ list, table: menu.table.index, row: menu.row, replacements: [], columns: [], remove_empty_rows_below: blankRowsBelow(menu.table, menu.row) > 0 });
              setMenu(null);
            }}
          >
            {t(`presentations.word.editor.each.${list}`)}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
